import type { MetaFunction } from '@remix-run/node';
import { Link } from '@remix-run/react';
import { ArrowLeft } from 'lucide-react';

export const meta: MetaFunction = () => [
  { title: 'Privacy Policy — Deleg8 Studio' },
  { name: 'description', content: 'How Deleg8 Studio handles your data. We never train AI on your brand data and you retain full ownership of generated content.' },
];

export default function Privacy() {
  return (
    <div className="min-h-screen bg-surface-950 text-white">
      <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-24">
        <Link
          to="/"
          className="mb-12 inline-flex items-center gap-2 text-sm text-white/60 transition hover:text-white/80"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </Link>

        <h1 className="text-4xl font-bold">Privacy Policy</h1>
        <p className="mt-4 text-sm text-white/60">Last updated: March 6, 2026</p>

        <div className="mt-12 space-y-10 text-sm leading-relaxed text-white/60">
          <section>
            <h2 className="text-lg font-semibold text-white">1. Data We Collect</h2>
            <p className="mt-3">
              When you use Deleg8 Studio, we collect information you provide directly: your email address when creating an account, brand materials you upload (websites, documents, assets), and content you generate through the platform.
            </p>
            <p className="mt-2">
              We also collect usage data automatically: pages visited, features used, device type, browser type, and IP address. We use Google Analytics and reCAPTCHA for analytics and spam protection.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">2. How We Use Your Data</h2>
            <p className="mt-3">We use your data to:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Provide and improve the Deleg8 Studio platform</li>
              <li>Generate campaign content based on your brand inputs</li>
              <li>Communicate with you about your account and product updates</li>
              <li>Analyze usage patterns to improve the product</li>
              <li>Prevent fraud and abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">3. AI and Your Brand Data</h2>
            <p className="mt-3">
              <strong className="text-white">We never train AI models on your brand data.</strong> Your brand materials are processed solely to generate content for your use. AI providers (Anthropic for Claude) process your data under strict data processing agreements and do not use it for model training.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">4. Data Sharing</h2>
            <p className="mt-3">We do not sell your data. We share data only with:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>AI service providers (Anthropic) to process your brand content — under strict data processing agreements</li>
              <li>Cloud infrastructure providers (Google Cloud) for hosting</li>
              <li>Analytics providers (Google Analytics) for usage metrics</li>
              <li>Law enforcement when legally required</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">5. Content Ownership</h2>
            <p className="mt-3">
              <strong className="text-white">You retain full ownership of all content generated by Deleg8 Studio.</strong> Your brand materials remain yours. Generated campaigns, copy, and visuals are yours to use however you see fit.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">6. Data Security</h2>
            <p className="mt-3">
              All data is encrypted at rest and in transit using industry-standard encryption. We use secure cloud infrastructure with access controls, monitoring, and regular security reviews.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">7. Data Retention</h2>
            <p className="mt-3">
              We retain your account data and brand materials for as long as your account is active. You can request deletion of your data at any time.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">8. Cookies</h2>
            <p className="mt-3">
              We use essential cookies for authentication and session management. We use Google Analytics cookies for usage metrics. We use reCAPTCHA cookies for spam protection. You can disable non-essential cookies in your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">9. Your Rights</h2>
            <p className="mt-3">You have the right to:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and data</li>
              <li>Export your data</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white">10. Contact</h2>
            <p className="mt-3">
              For privacy questions or data requests, email us at{' '}
              <a href="mailto:privacy@deleg8.dev" className="text-creative-400 hover:underline">
                privacy@deleg8.dev
              </a>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
